import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Grid, Paper, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  DeleteOutline as RemoveIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

import api from '../../utils/functions/api';
import { invalidateSaleCompany } from '../../utils/saleCompany';

const apiBase = process.env.REACT_APP_API_URL;
const MAX_IMAGE_MB = 2; // ตรงกับเพดานของ POST /sale-company/{logo,seal,signature} (saleCompany.routes.js)
const ACCEPT_IMAGE = 'image/png,image/jpeg,image/webp';

const EMPTY_FORM = {
  name: '',
  name_eng: '',
  tax_id: '',
  phone: '',
  email: '',
  website: '',
  note: '',
  tagline: '',
  logo_url: '',
  seal_url: '',
  signature_url: '',
  address: {
    house_number: '',
    soi: '',
    village_number: '',
    road: '',
    subdistrict: '',
    district: '',
    province: '',
    postcode: '',
    country: '',
  },
};

const ADDRESS_FIELDS = [
  ['house_number', 'เลขที่'],
  ['village_number', 'หมู่'],
  ['soi', 'ซอย'],
  ['road', 'ถนน'],
  ['subdistrict', 'ตำบล/แขวง'],
  ['district', 'อำเภอ/เขต'],
  ['province', 'จังหวัด'],
  ['postcode', 'รหัสไปรษณีย์'],
  ['country', 'ประเทศ'],
];

// การ์ดอัปโหลดรูป 1 ใบ (โลโก้/ตราประทับ/ลายเซ็น) — ใช้ input ซ่อนของตัวเอง
function ImageCard({ title, hint, url, uploading, onPick, onRemove }) {
  const inputRef = useRef(null);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
      <Typography fontWeight={700} sx={{ mb: 2 }}>{title}</Typography>
      <Box
        sx={{
          height: 140,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fff',
          overflow: 'hidden',
          mb: 2,
        }}
      >
        {url ? (
          <Box component="img" src={url} alt={title} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        ) : (
          <Typography variant="body2" color="text.secondary">ยังไม่มีรูป</Typography>
        )}
      </Box>
      <input ref={inputRef} type="file" accept={ACCEPT_IMAGE} hidden onChange={onPick} />
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {url ? 'เปลี่ยนรูป' : 'อัปโหลด'}
        </Button>
        {url && (
          <Button color="error" startIcon={<RemoveIcon />} onClick={onRemove}>ลบ</Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {hint} — PNG, JPG หรือ WebP ไม่เกิน {MAX_IMAGE_MB} MB กด "บันทึก" ด้านบนเพื่อยืนยัน
      </Typography>
    </Paper>
  );
}

// ข้อมูลบริษัท (มีได้บริษัทเดียว) ของเมนู "การขาย & เอกสาร" — เก็บใน collection ใหม่ของตัวเอง ไม่เกี่ยวกับ
// หน้า /management/business และโฟลเดอร์ org/ — ข้อมูลและรูปใช้ในหัว/ท้ายเอกสาร PDF (ตัวสร้าง PDF ดึงผ่าน utils/saleCompany.js)
export default function SaleCompany() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const notify = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  useEffect(() => {
    let active = true;
    api.get(`${apiBase}/sale-company`)
      .then(({ data }) => {
        if (active) {
          setForm({ ...EMPTY_FORM, ...data, address: { ...EMPTY_FORM.address, ...(data?.address || {}) } });
        }
      })
      .finally(() => active && setLoading(false));

    return () => { active = false; };
  }, []);

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const setAddress = (key) => (e) => setForm((prev) => ({ ...prev, address: { ...prev.address, [key]: e.target.value } }));

  // อัปโหลดรูปชนิดหนึ่ง (logo | seal | signature) — อัปแล้วแค่แสดงตัวอย่าง ยังไม่บันทึกจนกว่าจะกด "บันทึก"
  const handleImagePick = (kind, field) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      notify(`ไฟล์ใหญ่เกิน ${MAX_IMAGE_MB} MB`, 'error');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const { data } = await api.post(`${apiBase}/sale-company/${kind}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((prev) => ({ ...prev, [field]: data.url }));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`${apiBase}/sale-company`, form);
      setForm({ ...EMPTY_FORM, ...data, address: { ...EMPTY_FORM.address, ...(data?.address || {}) } });
      invalidateSaleCompany(); // ให้หน้าอื่น/การสร้าง PDF อ่านค่าใหม่ทันที
      notify('บันทึกข้อมูลบริษัทแล้ว');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>ข้อมูลบริษัท</Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || uploading}
          sx={{ borderRadius: 2 }}
        >
          บันทึก
        </Button>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <ImageCard
              title="โลโก้"
              hint="แสดงที่หัวเอกสาร"
              url={form.logo_url}
              uploading={uploading}
              onPick={handleImagePick('logo', 'logo_url')}
              onRemove={() => setForm((p) => ({ ...p, logo_url: '' }))}
            />
            <ImageCard
              title="ตราประทับ"
              hint="แสดงท้ายเอกสาร (แนะนำพื้นโปร่งใส)"
              url={form.seal_url}
              uploading={uploading}
              onPick={handleImagePick('seal', 'seal_url')}
              onRemove={() => setForm((p) => ({ ...p, seal_url: '' }))}
            />
            <ImageCard
              title="ลายเซ็นผู้อนุมัติ"
              hint="แสดงท้ายเอกสาร (แนะนำพื้นโปร่งใส)"
              url={form.signature_url}
              uploading={uploading}
              onPick={handleImagePick('signature', 'signature_url')}
              onRemove={() => setForm((p) => ({ ...p, signature_url: '' }))}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 3, mb: 3 }}>
            <Typography fontWeight={700} sx={{ mb: 2 }}>ข้อมูลทั่วไป</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="ชื่อบริษัท (ไทย)" value={form.name} onChange={setField('name')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="ชื่อบริษัท (อังกฤษ)" value={form.name_eng} onChange={setField('name_eng')} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="คำขวัญ / คำอธิบายใต้ชื่อบริษัท"
                  helperText="แสดงใต้ชื่อบริษัทในหัวเอกสาร (เว้นว่างได้)"
                  value={form.tagline}
                  onChange={setField('tagline')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="เลขประจำตัวผู้เสียภาษี" value={form.tax_id} onChange={setField('tax_id')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="เบอร์โทร" value={form.phone} onChange={setField('phone')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="อีเมล" value={form.email} onChange={setField('email')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="เว็บไซต์" value={form.website} onChange={setField('website')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={2} label="หมายเหตุ" value={form.note} onChange={setField('note')} />
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
            <Typography fontWeight={700} sx={{ mb: 2 }}>ที่อยู่</Typography>
            <Grid container spacing={2}>
              {ADDRESS_FIELDS.map(([key, label]) => (
                <Grid item xs={12} sm={4} key={key}>
                  <TextField fullWidth size="small" label={label} value={form.address[key]} onChange={setAddress(key)} />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
